<script setup lang="ts">
import { routerBack } from "@/use/go";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import {
  useBGM,
  useCharacterAudio,
  useTextBlip,
} from "../../composable/useGameSound";
import { FrameMode } from "../../enums/uiEnums";
import { BaseHouseScene } from "../../scenes/BaseHouseScene";
import { HouseScene } from "../../scenes/HouseScene";
import { useGameUI } from "../../store/gameUI";
import { useHouseScene } from "../../store/houseScene";
import { useHouseSession } from "../../store/houseSession";
import RadioPlayerControl, {
  RadioPlayerControlInstance,
} from "../panel/radio/RadioPlayerControl.vue";
import DialogueBox from "./DialogueBox.vue";
import { useNotification } from "@/stores/notification";

const props = defineProps<{
  scene: BaseHouseScene;
}>();
const emit = defineEmits<{
  (e: "show-invite-popup"): void;
}>();
const router = useRouter();
const gameUIStore = useGameUI();
const houseSceneStore = useHouseScene();
const houseSessionStore = useHouseSession();
const notification = useNotification();
const { validSprites } = storeToRefs(houseSceneStore);
const mateList = computed(() => {
  return validSprites.value.map((m) => m.mate) ?? [];
});
const mineMate = computed(() => {
  return mateList.value?.[0];
});
// #region SFX
const audioUrl = ref("");
const isDialoguePlaying = ref(false);
const bgmUrl = computed(() => {
  return gameUIStore.dialogue.dialogueConfig?.bgm?.url ?? "";
});
const { stop: stopTextBlip } = useTextBlip(isDialoguePlaying);
const { play: playBGM, stop: stopBGM } = useBGM(bgmUrl);
const { stop: stopAudio } = useCharacterAudio(audioUrl);
// #endregion
const showControl = computed(() => gameUIStore.isFreeMode);

const radioPlayerControlRef = ref<RadioPlayerControlInstance>();
// 播放历史事件的时候返回回到小屋页面
const clickBack = () => {
  stopAllSound();
  if (houseSceneStore.interval) routerBack();
  else {
    props.scene.scene.remove("HouseEvent");
    props.scene.game.scene.add("House", HouseScene, true);
  }
};

const onSubjectClick = () => {
  if (gameUIStore.frameMode === FrameMode.WITH_PANEL) {
    router.replace({ name: "dreamHome", query: { tab: "subject" } });
  }
};

const toggleBGM = (toggle: boolean) => {
  if (toggle) {
    playBGM();
  } else {
    stopBGM();
  }
};

// #region SFX Destroy
const stopAllSound = () => {
  stopAudio();
  stopBGM();
  stopTextBlip();
};

// 离开页面停止播放
onBeforeUnmount(() => {
  stopAllSound();
});

// 对话框关闭时停止播放
watch(
  () => gameUIStore.dialogue.isVisible,
  (newIsVisible) => {
    if (newIsVisible) return;
    stopAllSound();
  },
  { flush: "pre" }
);
// #endregion
</script>

<template>
  <div ref="gameUIRef">
    <div
      class="grid grid-cols-[auto_1fr] gap-2 absolute top-2 left-2 z-20 items-center"
      :class="{
        'mr-[3.6rem]': gameUIStore.frameMode === FrameMode.WITH_PANEL,
      }"
    >
      <button
        v-if="gameUIStore.frameMode === FrameMode.FULL_SCREEN"
        class="btn btn-circle bg-white/50 border-transparent btn-sm hover:bg-white/50 pointer-events-auto"
        @click="clickBack"
      >
        <i class="iconify mingcute--left-fill text-black" />
      </button>
      <div
        v-if="showControl && houseSessionStore.houseSession?.subject"
        class="text-white font-[Cubic] bg-black/50 p-2 rounded cursor-pointer pointer-events-auto"
        @click="onSubjectClick"
      >
        <span class="line-clamp-2"
          >当前话题: {{ houseSessionStore.houseSession?.subject }}</span
        >
      </div>
    </div>

    <div
      v-if="showControl"
      class="game-zoom-btn-group join absolute bottom-1 right-1 rounded bg-base-200/50 pointer-events-auto"
    >
      <!-- <button
        class="btn btn-xs join-item bg-base-200/50"
        @click="scene?.zoomIn"
      >
        <i class="iconify mingcute--add-line" />
      </button>
      <button
        class="btn btn-xs join-item bg-base-200/50"
        @click="scene?.zoomOut"
      >
        <i class="iconify ph--minus-bold" />
      </button> -->
      <button
        class="btn btn-xs join-item bg-base-200/50"
        @click="
          gameUIStore.frameMode =
            gameUIStore.frameMode === FrameMode.WITH_PANEL
              ? FrameMode.FULL_SCREEN_WITH_CONTROL
              : FrameMode.WITH_PANEL
        "
      >
        {{
          gameUIStore.frameMode === FrameMode.FULL_SCREEN_WITH_CONTROL
            ? "退出全屏"
            : "全屏"
        }}
        <i class="iconify mingcute--fullscreen-line" />
      </button>
    </div>
    <div
      v-if="
        showControl &&
        gameUIStore.frameMode !== FrameMode.FULL_SCREEN_WITH_CONTROL
      "
      class="flex flex-col absolute right-1 top-1 gap-1"
    >
      <!-- <button
        class="game-ui-right-btn"
        @click="router.push({ name: 'treehole', query: { isSub: 'true' } })"
      >
        <i
          class="fluent-emoji-high-contrast--fish-cake-with-swirl iconify text-red-600 text-base"
        />
        {{ $t("Buttons.treehole") }}
      </button> -->
      <!-- <button class="game-ui-right-btn" @click="emit('show-invite-popup')">
        <i
          class="iconify ph--address-book-tabs-light text-purple-500 text-base"
        />
        {{ $t("house.invite") }}
      </button> -->
      <button
        class="game-ui-right-btn relative"
        @click="radioPlayerControlRef?.showControl"
      >
        <div
          v-if="houseSceneStore.radio.isPlaying"
          class="absolute top-0 right-0 text-xs flex items-end justify-center text-primary"
        >
          <i class="iconify mingcute--play-circle-fill scale-[0.7]" />
        </div>
        <div
          class="w-[1.5rem] h-[1.5rem] relative"
          :style="{
            backgroundImage: `url(/game/items/radio.png)`,
            backgroundSize: 'cover',
          }"
        ></div>
        电台
      </button>
      <button
        class="game-ui-right-btn indicator w-14"
        @click="router.push({ name: 'interactionReview' })"
      >
        <span
          class="indicator-item badge badge-primary rounded-full badge-xs text-primary-content p-0 w-5 h-5 right-1.5 top-1"
          v-if="notification.interactionSummary?.pendingInteractionReviewCount"
        >
          {{ notification.interactionSummary?.pendingInteractionReviewCount }}
        </span>
        <i class="iconify fluent--task-list-square-rtl-16-regular text-base" />
        {{ $t("interactive.review") }}
      </button>
      <button
        class="game-ui-right-btn"
        @click="router.push({ name: 'furnishList' })"
      >
        <i class="iconify mingcute--paint-2-line" />
        装修
      </button>
      <button
        class="game-ui-right-btn"
        @click="router.push({ name: 'worldMissionList' })"
      >
        <i class="iconify mingcute--task-line" />
        任务
      </button>
    </div>

    <DialogueBox
      v-if="
        gameUIStore.dialogue.isVisible && gameUIStore.dialogue.dialogueConfig
      "
      :dialogue-config="gameUIStore.dialogue.dialogueConfig!"
      :mate-list="mateList"
      :mine-mate="mineMate"
      :sprite-list="validSprites"
      @update:dialogue-config="gameUIStore.dialogue.dialogueConfig = $event"
      @over="gameUIStore.dialogueOver"
      @update:is-playing="isDialoguePlaying = $event"
      @audio-change="audioUrl = $event"
      @toggle-bgm="toggleBGM"
    />
    <RadioPlayerControl
      v-if="showControl"
      :class="{
        hidden: !showControl,
      }"
      ref="radioPlayerControlRef"
    />
  </div>
</template>

<style scoped>
.game-ui {
  display: flex;
  align-items: center;
  justify-content: center;
  transform-origin: center;
}

.game-ui-right-btn {
  @apply flex flex-col border-2 border-white/50 rounded aspect-square items-center gap-1 bg-base-100 text-xs pointer-events-auto px-3 py-1.5;
}
</style>
